
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Date} from '../core/Date';
import {FieldModelDate} from '../core/Date';

/**
 * BillingStatisticsUpdateNotify struct
 */
class BillingStatisticsUpdateNotify {
  /**
   * Initialize struct
   * @param {!Date=} date
   * @constructor
   */
  constructor (date = new Date()) {
    this.Date = date
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BillingStatisticsUpdateNotify}
   * @param {!BillingStatisticsUpdateNotify} other Other struct
   * @returns {!BillingStatisticsUpdateNotify} This struct
   */
  copy (other) {
    if (other.Date != null) {
      this.Date = Date.fromObject(other.Date)
    } else {
      this.Date = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BillingStatisticsUpdateNotify}
   * @returns {!BillingStatisticsUpdateNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BillingStatisticsUpdateNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BillingStatisticsUpdateNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BillingStatisticsUpdateNotify}
   * @param {!BillingStatisticsUpdateNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BillingStatisticsUpdateNotify)) {
      throw new TypeError('Instance of BillingStatisticsUpdateNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BillingStatisticsUpdateNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Date: ((this.Date != null) ? this.Date : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BillingStatisticsUpdateNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BillingStatisticsUpdateNotify} other Object value
   * @returns {!BillingStatisticsUpdateNotify} Created struct
   */
  static fromObject (other) {
    return new BillingStatisticsUpdateNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BillingStatisticsUpdateNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BillingStatisticsUpdateNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BillingStatisticsUpdateNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10055
  }
}

export { BillingStatisticsUpdateNotify };

/**
 * Fast Binary Encoding BillingStatisticsUpdateNotify field model
 */
class FieldModelBillingStatisticsUpdateNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Date = new FieldModelDate(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Date.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Date.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBillingStatisticsUpdateNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10055
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Date.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Date.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!BillingStatisticsUpdateNotify} fbeValue Default value, defaults is new BillingStatisticsUpdateNotify()
   * @returns {!BillingStatisticsUpdateNotify} BillingStatisticsUpdateNotify value
   */
  get (fbeValue = new BillingStatisticsUpdateNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!BillingStatisticsUpdateNotify} fbeValue BillingStatisticsUpdateNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Date.fbeSize) <= fbeStructSize) {
      fbeValue.Date = this.Date.get()
    } else {
      fbeValue.Date = new Date()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Date.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {!BillingStatisticsUpdateNotify} fbeValue BillingStatisticsUpdateNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBillingStatisticsUpdateNotify}
   * @param {BillingStatisticsUpdateNotify} fbeValue BillingStatisticsUpdateNotify value
   */
  setFields (fbeValue) {
    this.Date.set(fbeValue.Date)
  }
}

export { FieldModelBillingStatisticsUpdateNotify };

/**
 * Fast Binary Encoding BillingStatisticsUpdateNotify model
 */
class BillingStatisticsUpdateNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBillingStatisticsUpdateNotify(this.buffer, 4)
  }

  /**
   * Get the BillingStatisticsUpdateNotify model
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!FieldModelBillingStatisticsUpdateNotify} model BillingStatisticsUpdateNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BillingStatisticsUpdateNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBillingStatisticsUpdateNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @param {!BillingStatisticsUpdateNotify} value BillingStatisticsUpdateNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @param {!BillingStatisticsUpdateNotify} value BillingStatisticsUpdateNotify value, defaults is new BillingStatisticsUpdateNotify()
   * @return {!object} Deserialized BillingStatisticsUpdateNotify value and its size
   */
  deserialize (value = new BillingStatisticsUpdateNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BillingStatisticsUpdateNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BillingStatisticsUpdateNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BillingStatisticsUpdateNotifyModel}
   * @param {!number} prev Previous BillingStatisticsUpdateNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BillingStatisticsUpdateNotifyModel };
